import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import ImageUploadButton from "../../components/admin/ImageUploadButton";

const KINDS = ["管理物件", "開発案件", "土地活用"];
const STATUSES = ["販売中", "開発中", "管理中", "応相談"];
const EMPTY = { title: "", kind: "管理物件", status: "応相談", location: "", area: "", price: "", description: "", image_url: "", published: true };

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function AdminRealEstate() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/admin/properties")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("取得に失敗しました"));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditing({});
  };

  const openEdit = (p) => {
    setForm({ title: p.title, kind: p.kind, status: p.status, location: p.location, area: p.area, price: p.price, description: p.description, image_url: p.image_url, published: p.published });
    setEditing(p);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/properties/${editing.id}`, form);
      else await api.post("/admin/properties", form);
      toast.success("保存しました");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`「${p.title}」を削除しますか？`)) return;
    try {
      await api.delete(`/admin/properties/${p.id}`);
      toast.success("削除しました");
      load();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div data-testid="admin-real-estate-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">不動産物件の管理</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">REAL ESTATE MANAGEMENT</p>
        </div>
        <button
          data-testid="property-add-button"
          onClick={openNew}
          className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover"
        >
          <Plus className="h-4 w-4" />
          新規追加
        </button>
      </div>

      <div className="mt-8 overflow-x-auto border border-line">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-coal font-mono text-[10px] tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">タイトル</th>
              <th className="px-4 py-3">区分</th>
              <th className="px-4 py-3">状態</th>
              <th className="px-4 py-3">所在地</th>
              <th className="px-4 py-3">価格</th>
              <th className="px-4 py-3">公開</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid={`property-row-${p.id}`} className="border-b border-line bg-panel last:border-b-0">
                <td className="px-4 py-3 text-xs font-bold text-white">{p.title}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400">{p.kind}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-accent">{p.status}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400">{p.location}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400">{p.price}</td>
                <td className="px-4 py-3 text-xs">{p.published ? <span className="text-green-400">公開</span> : <span className="text-gray-500">非公開</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`property-edit-${p.id}`} onClick={() => openEdit(p)} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`property-delete-${p.id}`} onClick={() => remove(p)} className="border border-line p-2 text-gray-400 transition-colors hover:border-red-500 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="property-form" onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{editing.id ? "物件を編集" : "物件を追加"}</h2>
              <button type="button" data-testid="property-form-close" onClick={() => setEditing(null)} className="text-gray-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <Field label="タイトル *">
              <input data-testid="property-form-title" required value={form.title} onChange={set("title")} className={inputCls} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="区分 *">
                <select data-testid="property-form-kind" value={form.kind} onChange={set("kind")} className={inputCls}>
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="状態">
                <select data-testid="property-form-status" value={form.status} onChange={set("status")} className={inputCls}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="所在地">
                <input data-testid="property-form-location" value={form.location} onChange={set("location")} className={inputCls} placeholder="三重県津市" />
              </Field>
              <Field label="面積">
                <input data-testid="property-form-area" value={form.area} onChange={set("area")} className={inputCls} placeholder="土地 320㎡" />
              </Field>
              <Field label="価格・賃料">
                <input data-testid="property-form-price" value={form.price} onChange={set("price")} className={inputCls} placeholder="1,280万円" />
              </Field>
            </div>
            <Field label="説明">
              <textarea data-testid="property-form-description" rows={3} value={form.description} onChange={set("description")} className={`resize-y ${inputCls}`} />
            </Field>
            <Field label="画像URL">
              <div className="flex items-start gap-2">
                <input data-testid="property-form-image" value={form.image_url} onChange={set("image_url")} className={`flex-1 ${inputCls}`} placeholder="https://..." />
                <ImageUploadButton testid="property-form-image-upload" onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} />
              </div>
            </Field>
            <label className="flex items-center gap-3 text-sm text-gray-300">
              <input data-testid="property-form-published" type="checkbox" checked={form.published} onChange={set("published")} className="h-4 w-4 accent-accent" />
              公開する
            </label>
            <button data-testid="property-form-submit" type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : "保存する"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
